// Modul „Analýza historie prodeje" — měsíční matice prodejů + Pareto pohledy.
// Data: GET /api/sales-history/monthly (produkt × měsíc, ks i tržby CZK).
// Taby Všechny / TOP 20 % / 80 % tržeb se počítají z téhož datasetu.
// Stockouty, meziroční, sezónní křivka a size-curve = příští iterace.

import { ApiError, apiFetch } from '@/lib/apiFetch';
import {
  downloadCsv,
  fillPrintWindow,
  openBlankWindow,
  printPlanInWindow,
  printTable,
} from '@/lib/export';
import { fmtNum } from '@/lib/format';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

type Cell = { units: number; revenue: number };
type ProductRow = {
  product_code: string;
  product_name: string;
  total_units: number;
  total_revenue: number;
  cells: Record<string, Cell>;
  image_url?: string | null;
};
type MonthlyResp = {
  months: string[];
  products: ProductRow[];
  monthly_totals: Record<string, Cell>;
  grand: { units: number; revenue: number };
  from: string;
};

type VariantRow = {
  variant_code: string;
  variant_name: string;
  total_units: number;
  total_revenue: number;
  cells: Record<string, Cell>;
};

type Metric = 'revenue' | 'units';
type TabId =
  | 'dashboard'
  | 'all'
  | 'top20'
  | 'pareto80'
  | 'stockouts'
  | 'plan'
  | 'season'
  | 'sizecurve';

const TABS: { id: TabId; label: string; ready: boolean }[] = [
  { id: 'dashboard', label: 'Dashboard', ready: true },
  { id: 'all', label: 'Všechny produkty', ready: true },
  { id: 'top20', label: 'TOP 20 % produktů', ready: true },
  { id: 'pareto80', label: '80 % tržeb', ready: true },
  { id: 'stockouts', label: 'Stockouty', ready: true },
  { id: 'plan', label: 'Plán přípravy', ready: true },
  { id: 'season', label: 'Sezónní křivka', ready: true },
  { id: 'sizecurve', label: 'Size-curve', ready: true },
];

// Měsíce — nominativ (nadpis) a genitiv (deadline „do 1. srpna").
const MONTH_NOM = [
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
];
const MONTH_GEN = [
  'ledna',
  'února',
  'března',
  'dubna',
  'května',
  'června',
  'července',
  'srpna',
  'září',
  'října',
  'listopadu',
  'prosince',
];

// Kompaktní formát tržeb pro buňky matice (úzké sloupce): 132029 → "132 k".
function fmtCompactCzk(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace('.', ',')} M`;
  if (n >= 1000) return `${Math.round(n / 1000)} k`;
  if (n === 0) return '·';
  return String(n);
}

function cellValue(metric: Metric, cell: Cell | undefined): number {
  if (!cell) return 0;
  return metric === 'revenue' ? cell.revenue : cell.units;
}

function fmtCell(metric: Metric, cell: Cell | undefined): string {
  const v = cellValue(metric, cell);
  if (v === 0) return '·';
  return metric === 'revenue' ? fmtCompactCzk(v) : fmtNum(v);
}

function fmtTotal(metric: Metric, n: number): string {
  return metric === 'revenue' ? `${fmtNum(n)} Kč` : `${fmtNum(n)} ks`;
}

// Pěkný název měsíce 'YYYY-MM' → 'čec 25'.
const MONTH_SHORT = [
  'led',
  'úno',
  'bře',
  'dub',
  'kvě',
  'čvn',
  'čvc',
  'srp',
  'zář',
  'říj',
  'lis',
  'pro',
];
function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-').map((s) => Number(s));
  if (!y || !m) return ym;
  return `${MONTH_SHORT[m - 1] ?? m} ${String(y).slice(2)}`;
}

// Rozsah pro filtr období: data začínají 1.7.2025, horní mez = dnešek.
const DATA_START_DATE = '2025-07-01';
const TODAY = (() => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
})();

// Posune 'YYYY-MM-DD' o n měsíců zpět; nikdy ne před začátek dat (1.7.2025).
function dateMonthsBack(dateStr: string, months: number): string {
  const [y, m, d] = dateStr.split('-').map((s) => Number(s));
  if (!y || !m || !d) return dateStr;
  const out = new Date(Date.UTC(y, m - 1 - months, d)).toISOString().slice(0, 10);
  return out < DATA_START_DATE ? DATA_START_DATE : out;
}

// Rychlé presety období (segmentovaný přepínač). Den-přesné rozsahy končící
// dnešním dnem; „Vše" = od začátku dat (1.7.2025) po dnešek.
const RANGE_PRESETS: { key: string; label: string; title?: string; from: string; to: string }[] = [
  { key: '3m', label: '3M', from: dateMonthsBack(TODAY, 3), to: TODAY },
  { key: '6m', label: '6M', from: dateMonthsBack(TODAY, 6), to: TODAY },
  { key: '12m', label: '12M', from: dateMonthsBack(TODAY, 12), to: TODAY },
  { key: 'all', label: 'Vše', title: 'Vše (1.7.2025 – dosud)', from: DATA_START_DATE, to: TODAY },
];

export function SalesHistoryPage() {
  const [data, setData] = useState<MonthlyResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('dashboard');
  const [metric, setMetric] = useState<Metric>('revenue');
  // Filtr období (měsíční přesnost), default = celé dostupné okno.
  const [from, setFrom] = useState<string>(DATA_START_DATE);
  const [to, setTo] = useState<string>(TODAY);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiFetch<MonthlyResp>(`/api/sales-history/monthly?from=${from}&to=${to}`)
      .then((d) => {
        if (!alive) return;
        setError(null);
        setData(d);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.describe() : 'Načtení selhalo');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to]);

  // Produkty pro daný tab. Backend řadí dle tržeb sestupně, takže TOP 20 %
  // i Pareto 80 % stačí odvodit z pořadí.
  const tabProducts = useMemo(() => {
    if (!data) return [];
    const all = data.products;
    if (tab === 'top20') {
      const n = Math.max(1, Math.ceil(all.length * 0.2));
      return all.slice(0, n);
    }
    if (tab === 'pareto80') {
      const target = data.grand.revenue * 0.8;
      const out: ProductRow[] = [];
      let cum = 0;
      for (const p of all) {
        out.push(p);
        cum += p.total_revenue;
        if (cum >= target) break;
      }
      return out;
    }
    return all;
  }, [data, tab]);

  // Plný rozsah preferuje highlight „Vše" (12M je s ním teď shodný).
  const activePreset =
    from === DATA_START_DATE && to === TODAY
      ? 'all'
      : (RANGE_PRESETS.find((p) => p.from === from && p.to === to)?.key ?? null);

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-text">Analýza historie prodeje</h1>
        <p className="text-sm text-text-secondary mt-0.5 max-w-2xl">
          Prodeje po měsících z databáze objednávek (Shoptet). Všechny tržby jsou{' '}
          <strong>bez DPH</strong>. Podklad pro řízení výroby — které produkty táhnou tržby, kdy
          mají sezónu a kdy jsme byli vyprodaní.
        </p>
        <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary">
          <span aria-hidden="true">🇨🇿</span>
          Data pouze z CZ e-shopu (v-mart.cz)
        </div>
      </div>

      {/* Filtr období (nad taby) — rozsah od–do + rychlé presety */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-surface px-2 py-1.5">
          <input
            type="date"
            value={from}
            min={DATA_START_DATE}
            max={to}
            onChange={(e) => setFrom(e.target.value || DATA_START_DATE)}
            className="cursor-pointer bg-transparent text-sm text-text outline-none"
            aria-label="Od"
          />
          <span className="text-text-muted">—</span>
          <input
            type="date"
            value={to}
            min={from}
            max={TODAY}
            onChange={(e) => setTo(e.target.value || TODAY)}
            className="cursor-pointer bg-transparent text-sm text-text outline-none"
            aria-label="Do"
          />
        </div>
        <div className="flex overflow-hidden rounded-lg border border-border bg-surface">
          {RANGE_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              title={p.title}
              onClick={() => {
                setFrom(p.from);
                setTo(p.to);
              }}
              className={`cursor-pointer px-3 py-2 text-sm font-medium transition-colors ${
                activePreset === p.key ? 'bg-primary text-white' : 'text-text-secondary hover:bg-bg'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Taby */}
      <div className="flex items-center gap-1 border-b border-border mb-4 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3.5 py-2 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors cursor-pointer ${
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text'
            }`}
          >
            {t.label}
            {!t.ready && <span className="ml-1.5 text-[10px] text-text-muted">brzy</span>}
          </button>
        ))}
      </div>

      {loading && <div className="text-sm text-text-muted py-10 text-center">Načítám data…</div>}
      {error && !loading && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Metrický přepínač — maticové taby + sezónní křivka */}
          {(tab === 'all' || tab === 'top20' || tab === 'pareto80' || tab === 'season') && (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-text-secondary">Metrika:</span>
              <MetricToggle metric={metric} onChange={setMetric} />
            </div>
          )}

          {tab === 'dashboard' && <DashboardTab data={data} />}
          {(tab === 'all' || tab === 'top20' || tab === 'pareto80') && (
            <>
              <TabBanner
                tab={tab}
                shown={tabProducts}
                total={data.products.length}
                grandRevenue={data.grand.revenue}
              />
              <MatrixTable
                key={`mt-${from}-${to}`}
                months={data.months}
                products={tabProducts}
                metric={metric}
                totals={data.monthly_totals}
                from={data.from}
                to={to}
              />
            </>
          )}
          {tab === 'stockouts' && <StockoutsTab from={from} to={to} />}
          {tab === 'plan' && <PlanTab data={data} from={from} to={to} />}
          {tab === 'season' && <SeasonTab data={data} metric={metric} />}
          {tab === 'sizecurve' && <SizeCurveTab data={data} from={from} to={to} />}
          {!TABS.find((t) => t.id === tab)?.ready && <ComingSoon tab={tab} />}
        </>
      )}
    </>
  );
}

function MetricToggle({ metric, onChange }: { metric: Metric; onChange: (m: Metric) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-border overflow-hidden">
      {(['revenue', 'units'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
            metric === m
              ? 'bg-primary text-white'
              : 'bg-surface text-text-secondary hover:bg-surface-hover'
          }`}
        >
          {m === 'revenue' ? 'Tržby (Kč, bez DPH)' : 'Počet ks'}
        </button>
      ))}
    </div>
  );
}

// Vysvětlující pruh nad maticí — kolik produktů tab ukazuje a jaký dělají podíl
// obratu. TOP 20 % i Pareto jsou prefixy seřazeného seznamu, takže bez tohohle
// vypadají na první pohled stejně jako „Všechny produkty".
function TabBanner({
  tab,
  shown,
  total,
  grandRevenue,
}: {
  tab: TabId;
  shown: ProductRow[];
  total: number;
  grandRevenue: number;
}) {
  const shownRevenue = shown.reduce((s, p) => s + p.total_revenue, 0);
  const share = grandRevenue > 0 ? Math.round((shownRevenue / grandRevenue) * 100) : 0;
  const countShare = total > 0 ? Math.round((shown.length / total) * 100) : 0;

  let text: string;
  if (tab === 'top20') {
    text = `Horních ${shown.length} z ${total} produktů (${countShare} %) s nejvyššími tržbami — dohromady ${share} % obratu bez DPH.`;
  } else if (tab === 'pareto80') {
    text = `Pareto: ${shown.length} z ${total} produktů (${countShare} %) tvoří ${share} % obratu bez DPH. Na ně se vyplatí soustředit řízení výroby.`;
  } else {
    text = `Celé portfolio — ${total} produktů s prodejem v rozsahu.`;
  }

  return (
    <div className="mb-3 rounded-lg border border-primary/20 bg-primary-50 px-4 py-2.5 text-sm text-text">
      {text}
    </div>
  );
}

function MatrixTable({
  months,
  products,
  metric,
  totals,
  from,
  to,
}: {
  months: string[];
  products: ProductRow[];
  metric: Metric;
  totals: Record<string, Cell>;
  from: string;
  to: string;
}) {
  // Drill-down: rozbalené produkty + cache načtených velikostí (lazy per produkt).
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [variants, setVariants] = useState<Record<string, VariantRow[] | 'loading' | 'error'>>({});

  async function toggle(code: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    const cached = variants[code];
    if (cached && cached !== 'error') return; // už načteno → jen přepni viditelnost
    setVariants((v) => ({ ...v, [code]: 'loading' }));
    try {
      const r = await apiFetch<{ variants: VariantRow[] }>(
        `/api/sales-history/monthly/variants?product=${encodeURIComponent(code)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );
      setVariants((v) => ({ ...v, [code]: r.variants }));
    } catch {
      setVariants((v) => ({ ...v, [code]: 'error' }));
    }
  }

  if (products.length === 0) {
    return (
      <div className="text-sm text-text-muted py-10 text-center">Žádná data v tomto rozsahu.</div>
    );
  }
  // Max hodnota pro „heat" pozadí buněk (vizuální čtení sezónnosti).
  const max = Math.max(
    1,
    ...products.flatMap((p) => months.map((m) => cellValue(metric, p.cells[m]))),
  );

  // Tlustá svislá čára na sloupci, kde začíná nový rok (mezi pro a led) —
  // ať je vizuálně jasný přechod roku. Třída se přidá na všechny buňky daného
  // sloupce (hlavička, produkty, varianty, patička), aby čára šla celou tabulkou.
  const yearSep = (i: number): string => {
    if (i <= 0) return '';
    const cur = months[i];
    const prev = months[i - 1];
    return cur && prev && cur.slice(0, 4) !== prev.slice(0, 4)
      ? ' border-l-2 border-l-text-secondary'
      : '';
  };

  return (
    <div className="overflow-x-auto border border-border rounded-xl bg-surface">
      <table className="text-sm border-collapse min-w-full">
        <thead>
          <tr className="border-b border-border">
            <th className="sticky left-0 z-10 bg-surface text-left font-semibold text-text px-3 py-2.5 min-w-[220px]">
              Produkt
            </th>
            {months.map((m, i) => (
              <th
                key={m}
                className={`text-right font-medium text-text-secondary px-2.5 py-2.5 whitespace-nowrap${yearSep(i)}`}
              >
                {fmtMonth(m)}
              </th>
            ))}
            <th className="text-right font-semibold text-text px-3 py-2.5 whitespace-nowrap bg-bg/50">
              Celkem
            </th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const isOpen = expanded.has(p.product_code);
            const drill = variants[p.product_code];
            return (
              <Fragment key={p.product_code}>
                <tr className="border-b border-border/60 hover:bg-surface-hover">
                  <td className="sticky left-0 z-10 bg-surface px-3 py-2 align-top">
                    <button
                      type="button"
                      onClick={() => toggle(p.product_code)}
                      className="group flex items-start gap-1.5 text-left cursor-pointer"
                    >
                      <span className="mt-0.5 w-3 shrink-0 text-text-muted">
                        {isOpen ? '▾' : '▸'}
                      </span>
                      <span>
                        <span className="font-medium text-text leading-tight group-hover:text-primary">
                          {p.product_name}
                        </span>
                        <span className="block text-[11px] text-text-muted">{p.product_code}</span>
                      </span>
                    </button>
                  </td>
                  {months.map((m, i) => {
                    const v = cellValue(metric, p.cells[m]);
                    const intensity = v > 0 ? 0.06 + 0.5 * (v / max) : 0;
                    return (
                      <td
                        key={m}
                        className={`text-right px-2.5 py-2 tabular-nums whitespace-nowrap${yearSep(i)}`}
                        style={
                          intensity > 0
                            ? { backgroundColor: `rgba(37, 99, 235, ${intensity})` }
                            : undefined
                        }
                      >
                        {fmtCell(metric, p.cells[m])}
                      </td>
                    );
                  })}
                  <td className="text-right px-3 py-2 font-semibold text-text tabular-nums whitespace-nowrap bg-bg/40">
                    {fmtTotal(metric, metric === 'revenue' ? p.total_revenue : p.total_units)}
                  </td>
                </tr>

                {isOpen && drill === 'loading' && (
                  <tr className="bg-bg/30">
                    <td
                      colSpan={months.length + 2}
                      className="px-3 py-2 pl-9 text-xs text-text-muted"
                    >
                      Načítám velikosti…
                    </td>
                  </tr>
                )}
                {isOpen && drill === 'error' && (
                  <tr className="bg-bg/30">
                    <td colSpan={months.length + 2} className="px-3 py-2 pl-9 text-xs text-danger">
                      Načtení velikostí selhalo — klikni znovu.
                    </td>
                  </tr>
                )}
                {isOpen &&
                  Array.isArray(drill) &&
                  drill.map((vr) => (
                    <tr
                      key={vr.variant_code}
                      className="border-b border-border/40 bg-bg/30 text-text-secondary"
                    >
                      <td className="sticky left-0 z-10 bg-bg/30 px-3 py-1.5 pl-9">
                        <span className="text-[13px]">Velikost {vr.variant_name}</span>
                        <span className="block text-[10px] text-text-muted">{vr.variant_code}</span>
                      </td>
                      {months.map((m, i) => (
                        <td
                          key={m}
                          className={`text-right px-2.5 py-1.5 tabular-nums whitespace-nowrap text-[13px]${yearSep(i)}`}
                        >
                          {fmtCell(metric, vr.cells[m])}
                        </td>
                      ))}
                      <td className="text-right px-3 py-1.5 tabular-nums whitespace-nowrap bg-bg/50 text-[13px] font-medium">
                        {fmtTotal(metric, metric === 'revenue' ? vr.total_revenue : vr.total_units)}
                      </td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border font-semibold text-text">
            <td className="sticky left-0 z-10 bg-surface px-3 py-2.5">
              Celkem ({products.length} produktů)
            </td>
            {months.map((m, i) => (
              <td
                key={m}
                className={`text-right px-2.5 py-2.5 tabular-nums whitespace-nowrap${yearSep(i)}`}
              >
                {fmtCell(metric, totals[m])}
              </td>
            ))}
            <td className="text-right px-3 py-2.5 tabular-nums whitespace-nowrap bg-bg/40">
              {fmtTotal(
                metric,
                products.reduce(
                  (s, p) => s + (metric === 'revenue' ? p.total_revenue : p.total_units),
                  0,
                ),
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function DashboardTab({ data }: { data: MonthlyResp }) {
  // Slovní souhrn z dat — highlighty, které jinak musíš vyčíst z matice.
  const { products, grand, months } = data;
  const top = products[0];
  const topShare = top && grand.revenue > 0 ? (top.total_revenue / grand.revenue) * 100 : 0;

  // Kolik produktů tvoří 80 % tržeb (Pareto).
  let pareto = 0;
  let cum = 0;
  for (const p of products) {
    pareto++;
    cum += p.total_revenue;
    if (cum >= grand.revenue * 0.8) break;
  }
  const paretoShare = products.length > 0 ? (pareto / products.length) * 100 : 0;

  // Nejsilnější měsíc dle tržeb.
  let bestMonth = '';
  let bestRev = -1;
  for (const m of months) {
    const r = data.monthly_totals[m]?.revenue ?? 0;
    if (r > bestRev) {
      bestRev = r;
      bestMonth = m;
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card
          label="Tržby celkem (bez DPH)"
          value={`${fmtNum(grand.revenue)} Kč`}
          sub={`kumulativně · ${months.length} měsíců`}
        />
        <Card
          label="Prodáno kusů"
          value={`${fmtNum(grand.units)} ks`}
          sub={`kumulativně · ${months.length} měsíců`}
        />
        <Card label="Produktů" value={fmtNum(products.length)} sub="s prodejem v rozsahu" />
        <Card
          label="Nejsilnější měsíc"
          value={bestMonth ? fmtMonth(bestMonth) : '—'}
          sub={bestRev >= 0 ? `${fmtNum(bestRev)} Kč bez DPH` : undefined}
        />
      </div>

      <div className="bg-surface border border-border rounded-xl p-5 text-sm text-text leading-relaxed space-y-2">
        <p className="font-semibold text-text">Souhrn</p>
        {top && (
          <p>
            • Nejsilnější produkt je <strong>{top.product_name}</strong> ({top.product_code}) —{' '}
            <strong>{fmtNum(top.total_revenue)} Kč bez DPH</strong> ({topShare.toFixed(0)} % všech
            tržeb, {fmtNum(top.total_units)} ks).
          </p>
        )}
        <p>
          • <strong>{pareto}</strong> z {products.length} produktů ({paretoShare.toFixed(0)} %)
          tvoří <strong>80 % tržeb</strong> — na ně se vyplatí soustředit řízení výroby.
        </p>
        {bestMonth && (
          <p>
            • Prodejně nejsilnější měsíc v rozsahu je <strong>{fmtMonth(bestMonth)}</strong> (
            {fmtNum(bestRev)} Kč).
          </p>
        )}
        <p className="text-text-muted text-[13px] pt-1">
          Souhrn se bude rozšiřovat, jak přibudou taby (stockouty, sezónní křivky, meziroční
          srovnání).
        </p>
      </div>
    </div>
  );
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wider">
        {label}
      </div>
      <div className="text-xl font-bold text-text mt-1">{value}</div>
      {sub && <div className="text-xs text-text-secondary mt-0.5">{sub}</div>}
    </div>
  );
}

type StockoutProduct = {
  product_code: string;
  product_name: string;
  sold_units: number;
  sold_revenue: number;
  lost_units: number;
  lost_revenue: number;
  lost_units_cons: number;
  lost_revenue_cons: number;
  stockout_days_sum: number;
  variants_total: number;
  variants_out: number;
  true_demand_units: number;
  low_conf_variants: number;
};

type StockoutTotals = {
  sold_units: number;
  sold_revenue: number;
  lost_units: number;
  lost_revenue: number;
  lost_units_cons: number;
  lost_revenue_cons: number;
  variants_lost: number;
};

type StockoutVariantRow = {
  product_code: string;
  product_name: string;
  sold_units: number;
  sold_revenue: number;
  stockout_days: number;
  instock_days: number;
  velocity: number;
  lost_units: number;
  lost_revenue: number;
  low_confidence: boolean;
};

// 'SO1186/146' → '146'.
function sizeLabel(code: string): string {
  const i = code.lastIndexOf('/');
  return i === -1 ? code : code.slice(i + 1);
}

// Souhrnný banner: celkové ušlé tržby kvůli vyprodanosti za zvolené období.
function StockoutSummary({ totals }: { totals: StockoutTotals }) {
  const cons = totals.lost_revenue_cons;
  const opt = totals.lost_revenue;
  if (opt <= 0) return null;
  return (
    <div className="rounded-xl border border-danger/30 bg-danger-light/50 p-5">
      <div className="text-xs font-semibold uppercase tracking-wide text-danger">
        Celkem ušlé tržby kvůli vyprodanosti (bez DPH)
      </div>
      <div className="mt-1 text-2xl font-bold text-text">
        {fmtCompactCzk(cons)} – {fmtCompactCzk(opt)} Kč
      </div>
      <div className="mt-1 text-sm text-text-secondary">
        konzervativně {fmtNum(cons)} Kč … optimisticky {fmtNum(opt)} Kč · odhad{' '}
        {fmtNum(totals.lost_units_cons)}–{fmtNum(totals.lost_units)} neprodaných ks ·{' '}
        {fmtNum(totals.variants_lost)} vyprodaných velikostí
      </div>
      <div className="mt-1.5 text-xs text-text-muted">
        Rozsah: <strong className="text-text-secondary">konzervativní</strong> (extrapolace max na
        dobu, jak dlouho byl produkt skladem) …{' '}
        <strong className="text-text-secondary">optimistický</strong> (plná extrapolace). Modelový
        odhad, ne účetní fakt — mění se podle zvoleného období.
      </div>
    </div>
  );
}

const PRODUCT_HEADERS = [
  'Kód',
  'Produkt',
  'Prodáno (ks)',
  'Vyprodané velikosti',
  'Ztracené ks (odhad)',
  'Ztracené tržby bez DPH (Kč)',
  'Skutečná poptávka (ks)',
];
const VARIANT_HEADERS = [
  'Kód produktu',
  'Produkt',
  'Velikost',
  'Prodáno (ks)',
  'Vyprodané dny',
  'Ztracené ks (odhad)',
  'Ztracené tržby bez DPH (Kč)',
  'Skutečná poptávka (ks)',
  'Pozn.',
];

function StockoutsTab({ from, to }: { from: string; to: string }) {
  const [products, setProducts] = useState<StockoutProduct[] | null>(null);
  const [totals, setTotals] = useState<StockoutTotals | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [variantsCache, setVariantsCache] = useState<StockoutVariantRow[] | null>(null);
  const [exporting, setExporting] = useState(false);

  // Analýza vyprodanosti pro zvolené období (filtr nad taby). Pohyby skladu
  // se udržují automaticky (inkrementální sync z cronu), backfill už proběhl.
  useEffect(() => {
    let alive = true;
    setLoadingAnalysis(true);
    setVariantsCache(null); // při změně období neplatí cache variant pro export
    apiFetch<{ products: StockoutProduct[]; totals: StockoutTotals }>(
      `/api/sales-history/stockouts?from=${from}&to=${to}`,
    )
      .then((r) => {
        if (!alive) return;
        setProducts(r.products);
        setTotals(r.totals);
      })
      .catch(() => {
        if (!alive) return;
        setProducts([]);
        setTotals(null);
      })
      .finally(() => {
        if (alive) setLoadingAnalysis(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to]);

  async function ensureVariants(): Promise<StockoutVariantRow[]> {
    if (variantsCache) return variantsCache;
    const r = await apiFetch<{ variants: StockoutVariantRow[] }>(
      `/api/sales-history/stockouts/all-variants?from=${from}&to=${to}`,
    );
    setVariantsCache(r.variants);
    return r.variants;
  }

  const productRows = (): (string | number)[][] =>
    (products ?? []).map((p) => [
      p.product_code,
      p.product_name,
      p.sold_units,
      `${p.variants_out}/${p.variants_total}`,
      p.lost_units,
      p.lost_revenue,
      p.true_demand_units,
    ]);

  const variantRows = (vs: StockoutVariantRow[]): (string | number)[][] =>
    vs.map((v) => [
      v.product_code.split('/')[0] ?? v.product_code,
      v.product_name,
      sizeLabel(v.product_code),
      v.sold_units,
      v.stockout_days,
      v.lost_units,
      v.lost_revenue,
      v.sold_units + v.lost_units,
      v.low_confidence ? 'málo dat' : '',
    ]);

  const suffix = `${from}_${to}`;
  const period = `${from} – ${to}`;

  async function exportVariants(kind: 'csv' | 'pdf') {
    const w = kind === 'pdf' ? openBlankWindow() : null;
    setExporting(true);
    try {
      const vs = await ensureVariants();
      if (kind === 'csv') {
        downloadCsv(`stockouty_velikosti_${suffix}.csv`, VARIANT_HEADERS, variantRows(vs));
      } else if (w) {
        fillPrintWindow(w, `Stockouty — velikosti (${period})`, VARIANT_HEADERS, variantRows(vs));
      }
    } finally {
      setExporting(false);
    }
  }

  const hasData = !!products && products.length > 0;

  return (
    <div className="space-y-4">
      {totals && <StockoutSummary totals={totals} />}
      <StockoutHelp />
      {hasData && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface px-4 py-3">
          <span className="text-xs font-semibold text-text-secondary">Export:</span>
          {(
            [
              {
                label: 'CSV — produkty',
                fn: () =>
                  downloadCsv(`stockouty_produkty_${suffix}.csv`, PRODUCT_HEADERS, productRows()),
              },
              { label: 'CSV — s velikostmi', fn: () => exportVariants('csv') },
              {
                label: 'PDF — produkty',
                fn: () =>
                  printTable(`Stockouty — produkty (${period})`, PRODUCT_HEADERS, productRows()),
              },
              { label: 'PDF — s velikostmi', fn: () => exportVariants('pdf') },
            ] as const
          ).map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={b.fn}
              disabled={exporting}
              className="cursor-pointer rounded border border-border bg-bg px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              {b.label}
            </button>
          ))}
          <span className="text-[11px] text-text-muted">
            {exporting ? 'Připravuji…' : 'CSV otevřeš v Google Sheets / Excelu · za zvolené období'}
          </span>
        </div>
      )}
      {loadingAnalysis ? (
        <div className="py-10 text-center text-sm text-text-muted">Počítám vyprodanost…</div>
      ) : products && products.length > 0 ? (
        <StockoutTable products={products} from={from} to={to} />
      ) : (
        <div className="py-10 text-center text-sm text-text-muted">
          V tomto období žádné prodeje k vyhodnocení.
        </div>
      )}
    </div>
  );
}

function NedostatekDat() {
  return (
    <span className="inline-block rounded bg-warning-light px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
      nedostatek dat
    </span>
  );
}

// Rozbalovací vysvětlení metodiky (default otevřené) s příkladem na velikosti 122.
function StockoutHelp() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-primary/20 bg-primary-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-text"
      >
        <span className="text-text-muted">{open ? '▾' : '▸'}</span>
        Jak se počítá ztráta a „skutečná poptávka"?
      </button>
      {open && (
        <div className="space-y-4 border-t border-primary/15 px-4 py-4 text-sm text-text-secondary">
          <p>
            Z <strong className="text-text">ledgeru pohybů</strong> přesně víme, kolik dní byla
            každá velikost skladem vs. vyprodaná. Z{' '}
            <strong className="text-text">objednávek</strong> víme, kolik se prodalo,{' '}
            <em>když byla skladem</em>. Z toho odhadneme, o kolik jsme přišli během vyprodaných dní.
          </p>

          <div className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
              Příklad — velikost 122 (SO1186), sezóna srpen–listopad
            </div>
            <ol className="space-y-1.5">
              <li>
                <strong className="text-text">Skladem 86,7 dne</strong>, vyprodáno 35,3 dne (= 122
                dní sezóny). Za dny skladem se prodalo <strong className="text-text">180 ks</strong>{' '}
                za 86 197 Kč bez DPH.
              </li>
              <li>
                <strong className="text-text">1) Rychlost:</strong> 180 ÷ 86,7 ≈{' '}
                <strong className="text-text">2,1 ks/den</strong> (jen z dní skladem).
              </li>
              <li>
                <strong className="text-text">2) Předpoklad:</strong> kdyby byla skladem i těch 35
                vyprodaných dní, prodávala by se dál ~2,1 ks/den.
              </li>
              <li>
                <strong className="text-text">3) Ztracené ks:</strong> 2,1 × 35,3 ≈{' '}
                <strong className="text-danger">~73 ks</strong> neprodaných kvůli výpadku.
              </li>
              <li>
                <strong className="text-text">4) Ztracené tržby:</strong> 73 × ~479 Kč ≈{' '}
                <strong className="text-danger">~35 000 Kč</strong>.
              </li>
              <li className="pt-1">
                <strong className="text-text">Skutečná poptávka</strong> = 180 prodáno + 73 ztráta ={' '}
                <strong className="text-text">~253 ks</strong> → tolik mít příště nachystáno.
              </li>
            </ol>
          </div>

          <p>
            <strong className="text-text">Je to odhad</strong> — stojí na předpokladu „tempo prodeje
            by pokračovalo i během výpadku". Většinou rozumné, ne dokonalé (ve špičce mohla být
            poptávka vyšší; nebo si zákazník vzal jinou velikost).
          </p>

          <p>
            Proto ztrátu počítáme <strong className="text-text">jen při dostatku dat</strong>: ≥ 7
            dní skladem <em>a zároveň</em> ≥ 5 prodaných ks. Když to nevyjde (např. velikost byla
            skladem jen 2 dny), místo čísla ukážeme <NedostatekDat /> — vyprodané dny zůstanou (to
            je fakt z ledgeru), jen nepředstíráme, že známe ztrátu. Řazeno dle ztracených tržeb (bez
            DPH).
          </p>
        </div>
      )}
    </div>
  );
}

function StockoutTable({
  products,
  from,
  to,
}: {
  products: StockoutProduct[];
  from: string;
  to: string;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [variants, setVariants] = useState<
    Record<string, StockoutVariantRow[] | 'loading' | 'error'>
  >({});

  async function toggle(code: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
    const cached = variants[code];
    if (cached && cached !== 'error') return;
    setVariants((v) => ({ ...v, [code]: 'loading' }));
    try {
      const r = await apiFetch<{ variants: StockoutVariantRow[] }>(
        `/api/sales-history/stockouts/variants?product=${encodeURIComponent(code)}&from=${from}&to=${to}`,
      );
      setVariants((v) => ({ ...v, [code]: r.variants }));
    } catch {
      setVariants((v) => ({ ...v, [code]: 'error' }));
    }
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-text-secondary">
            <th className="sticky left-0 z-10 bg-surface px-3 py-2.5 text-left font-semibold text-text">
              Produkt
            </th>
            <th className="px-3 py-2.5 text-right font-medium">Prodáno</th>
            <th className="px-3 py-2.5 text-right font-medium">Vyprodané velikosti</th>
            <th className="px-3 py-2.5 text-right font-medium">Ztracené ks</th>
            <th className="px-3 py-2.5 text-right font-medium">Ztracené tržby</th>
            <th className="px-3 py-2.5 text-right font-semibold text-text">Skutečná poptávka</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const isOpen = expanded.has(p.product_code);
            const drill = variants[p.product_code];
            return (
              <Fragment key={p.product_code}>
                <tr className="border-b border-border/60 hover:bg-surface-hover">
                  <td className="sticky left-0 z-10 bg-surface px-3 py-2">
                    <button
                      type="button"
                      onClick={() => toggle(p.product_code)}
                      className="group flex items-start gap-1.5 text-left cursor-pointer"
                    >
                      <span className="mt-0.5 w-3 shrink-0 text-text-muted">
                        {isOpen ? '▾' : '▸'}
                      </span>
                      <span>
                        <span className="font-medium text-text leading-tight group-hover:text-primary">
                          {p.product_name}
                        </span>
                        <span className="block text-[11px] text-text-muted">{p.product_code}</span>
                      </span>
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                    {fmtNum(p.sold_units)} ks
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-text-secondary">
                    {p.variants_out}/{p.variants_total}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                    {p.lost_units > 0 ? (
                      `+${fmtNum(p.lost_units)}`
                    ) : p.low_conf_variants > 0 ? (
                      <NedostatekDat />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-danger">
                    {p.lost_revenue > 0 ? (
                      `${fmtNum(p.lost_revenue)} Kč`
                    ) : p.low_conf_variants > 0 ? (
                      <NedostatekDat />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-text">
                    {fmtNum(p.true_demand_units)} ks
                  </td>
                </tr>

                {isOpen && drill === 'loading' && (
                  <tr className="bg-bg/30">
                    <td colSpan={6} className="px-3 py-2 pl-9 text-xs text-text-muted">
                      Načítám velikosti…
                    </td>
                  </tr>
                )}
                {isOpen && drill === 'error' && (
                  <tr className="bg-bg/30">
                    <td colSpan={6} className="px-3 py-2 pl-9 text-xs text-danger">
                      Načtení selhalo — klikni znovu.
                    </td>
                  </tr>
                )}
                {isOpen &&
                  Array.isArray(drill) &&
                  drill.map((vr) => (
                    <tr
                      key={vr.product_code}
                      className="border-b border-border/40 bg-bg/30 text-[13px] text-text-secondary"
                    >
                      <td className="sticky left-0 z-10 bg-bg/30 px-3 py-1.5 pl-9">
                        Velikost {sizeLabel(vr.product_code)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                        {fmtNum(vr.sold_units)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                        {vr.stockout_days} dní vyprodáno
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                        {vr.lost_units > 0 ? (
                          `+${fmtNum(vr.lost_units)}`
                        ) : vr.low_confidence ? (
                          <NedostatekDat />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-danger">
                        {vr.lost_revenue > 0 ? (
                          `${fmtNum(vr.lost_revenue)} Kč`
                        ) : vr.low_confidence ? (
                          <NedostatekDat />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
                        {fmtNum(vr.sold_units + vr.lost_units)}
                      </td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Najde nejkratší souvislé okno měsíců pokrývající aspoň `frac` z celkového
// objemu — auto-detekce sezóny (kde se odehraje většina prodejů).
function seasonWindow(values: number[], frac: number): [number, number] | null {
  const total = values.reduce((a, b) => a + b, 0);
  if (total <= 0) return null;
  const target = total * frac;
  let best: [number, number] | null = null;
  let bestLen = Number.POSITIVE_INFINITY;
  for (let i = 0; i < values.length; i++) {
    let sum = 0;
    for (let j = i; j < values.length; j++) {
      sum += values[j] ?? 0;
      if (sum >= target) {
        if (j - i + 1 < bestLen) {
          bestLen = j - i + 1;
          best = [i, j];
        }
        break;
      }
    }
  }
  return best;
}

// Vyhledávací výběr produktu s fotkou (kód nebo název). Filtruje client-side
// z data.products (máme je už načtené).
function ProductPicker({
  products,
  value,
  onChange,
}: {
  products: ProductRow[];
  value: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);
  const selected = products.find((p) => p.product_code === value);
  const q = query.trim().toLowerCase();
  const matches = (
    q
      ? products.filter(
          (p) =>
            p.product_code.toLowerCase().includes(q) || p.product_name.toLowerCase().includes(q),
        )
      : products
  ).slice(0, 40);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-w-[20rem] max-w-lg cursor-pointer items-center gap-2 rounded-lg border border-border bg-surface px-2 py-1.5 text-left text-sm hover:border-primary"
      >
        {selected?.image_url ? (
          <img
            src={selected.image_url}
            alt=""
            loading="lazy"
            className="h-8 w-8 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="h-8 w-8 shrink-0 rounded bg-bg" />
        )}
        <span className="min-w-0 flex-1 truncate">
          {selected ? (
            <>
              <span className="text-text">{selected.product_name}</span>{' '}
              <span className="text-text-muted">({selected.product_code})</span>
            </>
          ) : (
            <span className="text-text-muted">Vyber produkt…</span>
          )}
        </span>
        <span className="text-text-muted">▾</span>
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-label="Zavřít"
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div className="absolute left-0 z-20 mt-1 max-h-96 w-[30rem] max-w-[90vw] overflow-auto rounded-lg border border-border bg-surface shadow-card">
            <div className="sticky top-0 bg-surface p-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Hledat podle kódu nebo názvu…"
                className="w-full rounded-md border border-border bg-bg px-3 py-1.5 text-sm text-text outline-none focus:border-primary"
              />
            </div>
            {matches.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-text-muted">Nic nenalezeno.</div>
            ) : (
              matches.map((p) => (
                <button
                  key={p.product_code}
                  type="button"
                  onClick={() => {
                    onChange(p.product_code);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={`flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-left hover:bg-surface-hover ${
                    p.product_code === value ? 'bg-primary-50' : ''
                  }`}
                >
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt=""
                      loading="lazy"
                      className="h-8 w-8 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 shrink-0 rounded bg-bg" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-text">{p.product_name}</span>
                    <span className="block text-[11px] text-text-muted">{p.product_code}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

// Rozbalovací vysvětlení sezónní křivky (default otevřené).
function SeasonHelp() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-primary/20 bg-primary-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-text"
      >
        <span className="text-text-muted">{open ? '▾' : '▸'}</span>
        Jak se počítá sezóna?
      </button>
      {open && (
        <div className="space-y-2 border-t border-primary/15 px-4 py-4 text-sm text-text-secondary">
          <p>
            Křivka ukazuje{' '}
            <strong className="text-text">prodeje vybraného produktu po měsících</strong> (přepínač
            Kč / ks nahoře).
          </p>
          <p>
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary align-middle" />{' '}
            <strong className="text-text">Sezóna</strong> (tmavě modrá) = nejkratší{' '}
            <strong className="text-text">
              souvislý blok měsíců, který dohromady tvoří 80 % prodejů
            </strong>
            . Bereme ji jako „kdy mít vyrobeno" — výroba musí být hotová ještě <em>před</em> jejím
            startem. Nejsilnější měsíc je navíc zvýrazněný rámečkem.
          </p>
          <p>
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary/25 align-middle" />{' '}
            <strong className="text-text">Mimo sezónu</strong> (světle modrá) = zbylé měsíce (cca 20
            % prodejů) — typicky doprodejový ocas nebo slabší část roku.
          </p>
          <p className="text-text-muted">
            Pozn.: sezóna je <strong className="text-text-secondary">souvislé okno</strong>, takže
            měsíc <em>těsně za jeho hranou</em> může mít ještě slušné prodeje (doprodej nebo druhý
            menší vrchol) a přesto je světle modrý. Hranice je orientační — čti křivku jako celek.
          </p>
        </div>
      )}
    </div>
  );
}

function SeasonTab({ data, metric }: { data: MonthlyResp; metric: Metric }) {
  const products = data.products;
  const [code, setCode] = useState(() => products[0]?.product_code ?? '');
  const product = products.find((p) => p.product_code === code) ?? products[0];

  if (!product) {
    return (
      <div className="py-10 text-center text-sm text-text-muted">Žádná data v tomto období.</div>
    );
  }

  const months = data.months;
  const values = months.map((m) => cellValue(metric, product.cells[m]));
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const unit = metric === 'revenue' ? ' Kč' : ' ks';
  const fmtVal = (v: number) => (metric === 'revenue' ? fmtCompactCzk(v) : fmtNum(v));

  let peakIdx = 0;
  values.forEach((v, i) => {
    if (v > (values[peakIdx] ?? 0)) peakIdx = i;
  });

  const season = seasonWindow(values, 0.8);
  const inSeason = (i: number) => season !== null && i >= season[0] && i <= season[1];
  const seasonLabel =
    season && months[season[0]] && months[season[1]]
      ? season[0] === season[1]
        ? fmtMonth(months[season[0]] as string)
        : `${fmtMonth(months[season[0]] as string)} – ${fmtMonth(months[season[1]] as string)}`
      : '—';

  return (
    <div className="space-y-4">
      <SeasonHelp />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-text-secondary">Produkt:</span>
        <ProductPicker products={products} value={code} onChange={setCode} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card
          label="Sezóna (80 % prodejů)"
          value={seasonLabel}
          sub="souvislé měsíce s většinou prodejů"
        />
        <Card
          label="Nejsilnější měsíc"
          value={months[peakIdx] ? fmtMonth(months[peakIdx] as string) : '—'}
          sub={`${fmtVal(values[peakIdx] ?? 0)}${unit}`}
        />
        <Card label="Celkem za období" value={`${fmtNum(total)}${unit}`} />
      </div>

      <div className="rounded-xl border border-border bg-surface p-5">
        <div className="flex items-end gap-1.5">
          {months.map((m, i) => {
            const v = values[i] ?? 0;
            const h = max > 0 ? (v / max) * 100 : 0;
            return (
              <div key={m} className="flex flex-1 flex-col items-center gap-1">
                <div className="h-4 text-[10px] tabular-nums text-text-muted">
                  {v > 0 ? fmtVal(v) : ''}
                </div>
                <div className="flex h-48 w-full items-end">
                  <div
                    className={`w-full rounded-t ${inSeason(i) ? 'bg-primary' : 'bg-primary/25'} ${
                      i === peakIdx ? 'ring-2 ring-primary-dark' : ''
                    }`}
                    style={{ height: `${v > 0 ? Math.max(h, 2) : 0}%` }}
                    title={`${fmtMonth(m)}: ${fmtVal(v)}${unit}`}
                  />
                </div>
                <div className="whitespace-nowrap text-[10px] text-text-secondary">
                  {fmtMonth(m)}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-text-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" /> sezóna
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary/25" /> mimo sezónu
          </span>
          <span>Sezóna = „kdy mít vyrobeno": výroba musí být hotová ještě před jejím startem.</span>
        </div>
      </div>
    </div>
  );
}

// Rozpočítá `total` ks dle vah na celá čísla tak, aby součet seděl přesně
// (largest-remainder: zbytek po zaokrouhlení dolů přidá největším zlomkům).
function distribute(weights: number[], total: number): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0) return weights.map(() => 0);
  const raw = weights.map((w) => (w / sum) * total);
  const out = raw.map((x) => Math.floor(x));
  let rem = total - out.reduce((a, b) => a + b, 0);
  const order = raw.map((x, i) => ({ i, f: x - Math.floor(x) })).sort((a, b) => b.f - a.f);
  for (const o of order) {
    if (rem <= 0) break;
    out[o.i] = (out[o.i] ?? 0) + 1;
    rem--;
  }
  return out;
}

// Rozbalovací vysvětlení size-curve (default otevřené).
function SizeHelp() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-primary/20 bg-primary-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-text"
      >
        <span className="text-text-muted">{open ? '▾' : '▸'}</span>
        Jak se počítá rozpad velikostí?
      </button>
      {open && (
        <div className="space-y-2 border-t border-primary/15 px-4 py-4 text-sm text-text-secondary">
          <p>
            Ukazuje <strong className="text-text">ideální poměr velikostí pro výrobu</strong> —
            kolik kterého rozměru naskladnit, aby rozpad odpovídal poptávce.
          </p>
          <p className="font-medium text-text">Základ rozpadu (přepínač):</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong className="text-text">Dle skutečné poptávky</strong> (default) = prodáno +
              odhad ztrát ze stockoutů. Přesnější — koriguje velikosti, co se v sezóně vyprodaly;
              jejich čistý prodej je podhodnocený a ve výrobě by vyšly falešně menší.
            </li>
            <li>
              <strong className="text-text">Dle prodaného</strong> = jen reálně prodané ks, bez
              korekce o výpadky.
            </li>
          </ul>
          <p>
            Zadáš <strong className="text-text">„Chci vyrobit X ks"</strong> a rozpočítám objem na
            velikosti podle podílů — <strong className="text-text">součet sedí přesně</strong> na
            zadané číslo (zbytek po zaokrouhlení dostanou velikosti s největší poptávkou).
          </p>
          <p className="text-text-muted">
            Tip: zkombinuj taby — <strong className="text-text-secondary">Stockouty</strong> řeknou
            kolik celkem vyrobit, <strong className="text-text-secondary">Size-curve</strong> v
            jakém rozpadu velikostí a{' '}
            <strong className="text-text-secondary">Sezónní křivka</strong> kdy to mít hotové.
          </p>
        </div>
      )}
    </div>
  );
}

function SizeCurveTab({ data, from, to }: { data: MonthlyResp; from: string; to: string }) {
  const products = data.products;
  const [code, setCode] = useState(() => products[0]?.product_code ?? '');
  const [basis, setBasis] = useState<'demand' | 'sold'>('demand');
  const [planned, setPlanned] = useState('');
  const [variants, setVariants] = useState<StockoutVariantRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) return;
    let alive = true;
    setLoading(true);
    apiFetch<{ variants: StockoutVariantRow[] }>(
      `/api/sales-history/stockouts/variants?product=${encodeURIComponent(code)}&from=${from}&to=${to}`,
    )
      .then((r) => {
        if (alive) setVariants(r.variants);
      })
      .catch(() => {
        if (alive) setVariants([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [code, from, to]);

  if (!products[0]) {
    return (
      <div className="py-10 text-center text-sm text-text-muted">Žádná data v tomto období.</div>
    );
  }

  const basisVal = (v: StockoutVariantRow) =>
    basis === 'demand' ? v.sold_units + v.lost_units : v.sold_units;
  const rows = (variants ?? []).filter((v) => v.sold_units > 0 || v.lost_units > 0);
  const total = rows.reduce((a, v) => a + basisVal(v), 0);
  const maxBasis = Math.max(1, ...rows.map(basisVal));
  const plannedN = Number(planned);
  const hasPlanned = Number.isFinite(plannedN) && plannedN > 0;
  const produced = hasPlanned ? distribute(rows.map(basisVal), Math.round(plannedN)) : null;

  return (
    <div className="space-y-4">
      {/* Ovládání: produkt + základ rozpadu + plánovaný objem */}
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-text-secondary">Produkt:</span>
        <ProductPicker products={products} value={code} onChange={setCode} />
        <div className="inline-flex overflow-hidden rounded-lg border border-border">
          {(['demand', 'sold'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBasis(b)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                basis === b ? 'bg-primary text-white' : 'bg-surface text-text-secondary hover:bg-bg'
              }`}
            >
              {b === 'demand' ? 'Dle skutečné poptávky' : 'Dle prodaného'}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-xs font-semibold text-text-secondary">
          Chci vyrobit:
          <input
            type="number"
            min="0"
            value={planned}
            onChange={(e) => setPlanned(e.target.value)}
            placeholder="ks"
            className="w-24 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm text-text outline-none focus:border-primary"
          />
        </label>
      </div>

      <SizeHelp />

      {loading ? (
        <div className="py-10 text-center text-sm text-text-muted">Načítám velikosti…</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-text-muted">
          Tento produkt nemá v období prodeje.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface">
          <table className="min-w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-text-secondary">
                <th className="px-3 py-2.5 text-left font-semibold text-text">Velikost</th>
                <th className="px-3 py-2.5 text-right font-medium">Prodáno</th>
                <th className="px-3 py-2.5 text-right font-medium">Ztracené</th>
                <th className="px-3 py-2.5 text-right font-medium">Skut. poptávka</th>
                <th className="px-3 py-2.5 text-left font-medium">Podíl</th>
                {hasPlanned && (
                  <th className="px-3 py-2.5 text-right font-semibold text-text">Vyrob (ks)</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => {
                const bv = basisVal(v);
                const share = total > 0 ? (bv / total) * 100 : 0;
                const barW = (bv / maxBasis) * 100;
                return (
                  <tr
                    key={v.product_code}
                    className="border-b border-border/60 hover:bg-surface-hover"
                  >
                    <td className="px-3 py-2 font-medium text-text">
                      Velikost {sizeLabel(v.product_code)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {fmtNum(v.sold_units)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      {v.lost_units > 0 ? (
                        `+${fmtNum(v.lost_units)}`
                      ) : v.low_confidence ? (
                        <NedostatekDat />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-text">
                      {fmtNum(v.sold_units + v.lost_units)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-2.5 w-28 overflow-hidden rounded-full bg-bg">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${barW}%` }}
                          />
                        </div>
                        <span className="tabular-nums text-text-secondary">
                          {share.toFixed(0)} %
                        </span>
                      </div>
                    </td>
                    {hasPlanned && (
                      <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-primary">
                        {fmtNum(produced?.[i] ?? 0)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-border font-semibold text-text">
                <td className="px-3 py-2.5">Celkem</td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmtNum(rows.reduce((a, v) => a + v.sold_units, 0))}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  +{fmtNum(rows.reduce((a, v) => a + v.lost_units, 0))}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">
                  {fmtNum(rows.reduce((a, v) => a + v.sold_units + v.lost_units, 0))}
                </td>
                <td className="px-3 py-2.5 text-text-secondary">100 %</td>
                {hasPlanned && (
                  <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-primary">
                    {fmtNum(Math.round(plannedN))}
                  </td>
                )}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

type MonthPlanItem = {
  product_code: string;
  product_name: string;
  image_url?: string | null;
  month_sold: number; // reálně prodáno v tomto měsíci (ks)
  demandLow: number; // poptávka měsíce: prodáno + konzerv. korekce o ztráty
  demandHigh: number; // prodáno + optim. korekce o ztráty
  revenue: number; // poptávka měsíce v Kč bez DPH (prodáno + ztráty)
};

type PlanSection = {
  cal: number;
  ym: string;
  items: MonthPlanItem[]; // zobrazené (po Pareto filtru)
  allCount: number; // celkem produktů v měsíci
  total: number; // ks poptávka (zobrazených)
  totalRev: number; // obrat zobrazených (Kč bez DPH)
};

type DemandCell = {
  sold: number;
  lost: number;
  lost_cons: number;
  sold_revenue: number;
  lost_revenue: number;
};
type MonthlyDemand = {
  months: string[];
  products: { product_code: string; product_name: string; cells: Record<string, DemandCell> }[];
};

type PlanExportRow = {
  ym: string;
  parent_code: string;
  product_name: string;
  size: string;
  demand: number;
  revenue: number;
};

function sizeNum(s: string): number {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

function PlanHelp() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-primary/20 bg-primary-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-sm font-semibold text-text"
      >
        <span className="text-text-muted">{open ? '▾' : '▸'}</span>
        Jak číst plán přípravy?
      </button>
      {open && (
        <div className="space-y-2 border-t border-primary/15 px-4 py-4 text-sm text-text-secondary">
          <p>
            Měsíc po měsíci: <strong className="text-text">kolik se v daném měsíci prodá</strong> —
            ať víš, co na něj mít nachystáno. Produkt se opakuje v každém měsíci, kdy se prodává.
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong className="text-text">Poptávka měsíce</strong> = reálně prodáno v tom měsíci +
              ztráty ze stockoutů spočítané{' '}
              <strong className="text-text">přímo za ten měsíc</strong> (shodné s tabem Stockouty s
              filtrem na daný měsíc), jako rozsah konzervativní…optimistický.
            </li>
            <li>
              <strong className="text-text">Priorita = obrat</strong> — u každého produktu je{' '}
              <strong className="text-text">≈ Kč obratu</strong> (poptávka × cena bez DPH) a
              produkty jsou seřazené dle něj sestupně. Přepínač{' '}
              <strong className="text-text">„Jen TOP (80 % obratu)"</strong> ukáže Paretem jen
              nejmenší množinu produktů, co dělají 80 % obratu měsíce (zbytek skryje).
            </li>
            <li>
              <strong className="text-text">Rozpad velikostí</strong> — klik na produkt rozbalí,
              kolik které velikosti na ten měsíc.
            </li>
          </ul>
          <p>
            Měsíce jsou seřazené od nejbližšího; deadline = mít hotovo do 1. dne měsíce. Klik na
            hlavičku měsíce ho rozbalí/sbalí.
          </p>
          <p className="text-text-muted">
            Pro plán nech filtr období na <strong className="text-text-secondary">„Vše"</strong>{' '}
            (celý rok). Stejná čísla uvidíš v tabu Stockouty, když filtr nastavíš na konkrétní
            měsíc.
          </p>
        </div>
      )}
    </div>
  );
}

// 'YYYY-MM' → poslední den měsíce 'YYYY-MM-DD'.
function lastDayOfMonth(ym: string): string {
  const [y, m] = ym.split('-').map((s) => Number(s));
  if (!y || !m) return `${ym}-28`;
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

function PlanRow({ item, ym }: { item: MonthPlanItem; ym: string }) {
  const [open, setOpen] = useState(false);
  const [variants, setVariants] = useState<StockoutVariantRow[] | 'loading' | 'error' | null>(null);

  async function toggle() {
    setOpen((o) => !o);
    if (variants) return;
    setVariants('loading');
    try {
      const r = await apiFetch<{ variants: StockoutVariantRow[] }>(
        `/api/sales-history/stockouts/variants?product=${encodeURIComponent(item.product_code)}&from=${ym}-01&to=${lastDayOfMonth(ym)}`,
      );
      setVariants(r.variants);
    } catch {
      setVariants('error');
    }
  }

  const qtyLabel =
    item.demandLow === item.demandHigh
      ? `~${fmtNum(item.demandHigh)} ks`
      : `~${fmtNum(item.demandLow)}–${fmtNum(item.demandHigh)} ks`;

  // Rozpad přímo za daný měsíc (sold + lost per velikost) — lícuje na Stockouty.
  const sizeRows = Array.isArray(variants)
    ? variants
        .filter((v) => v.sold_units + v.lost_units > 0)
        .map((v) => ({ code: v.product_code, qty: v.sold_units + v.lost_units }))
    : [];

  return (
    <div className="rounded-lg border border-border bg-surface">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full cursor-pointer items-center gap-3 p-3 text-left hover:bg-surface-hover"
      >
        {item.image_url ? (
          <img
            src={item.image_url}
            alt=""
            loading="lazy"
            className="h-10 w-10 shrink-0 rounded object-cover"
          />
        ) : (
          <div className="h-10 w-10 shrink-0 rounded bg-bg" />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-text">{item.product_name}</span>
          <span className="block text-[11px] text-text-muted">
            {item.product_code} · loni v tomto měsíci prodáno {fmtNum(item.month_sold)} ks
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span className="block text-sm font-bold text-text">≈ {fmtNum(item.revenue)} Kč</span>
          <span className="block text-xs font-medium text-primary">Prodá se {qtyLabel}</span>
          <span className="block text-[11px] text-text-muted">
            {open ? '▾ skrýt rozpad' : '▸ rozpad velikostí'}
          </span>
        </span>
      </button>
      {open && (
        <div className="border-t border-border/60 px-3 py-2.5 text-xs text-text-secondary">
          {variants === 'loading' ? (
            'Načítám rozpad…'
          ) : variants === 'error' ? (
            <span className="text-danger">Načtení selhalo.</span>
          ) : sizeRows.length === 0 ? (
            'Bez dat o velikostech.'
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {sizeRows.map((s) => (
                <span key={s.code} className="rounded bg-bg px-2 py-1 tabular-nums">
                  vel. {sizeLabel(s.code)}: <strong className="text-text">{fmtNum(s.qty)}</strong>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PlanTab({ data, from, to }: { data: MonthlyResp; from: string; to: string }) {
  const [demand, setDemand] = useState<MonthlyDemand | null>(null);
  const [loading, setLoading] = useState(false);
  const [paretoOnly, setParetoOnly] = useState(true);
  const [exportingPlan, setExportingPlan] = useState(false);

  // Per produkt × měsíc: prodáno + PŘESNÁ ztráta toho měsíce (shodné se Stockouty).
  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiFetch<MonthlyDemand>(`/api/sales-history/stockouts/monthly?from=${from}&to=${to}`)
      .then((r) => {
        if (alive) setDemand(r);
      })
      .catch(() => {
        if (alive) setDemand({ months: [], products: [] });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to]);

  // Obrázky produktů z měsíční matice (/monthly už máme načtené).
  const imageByCode = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const p of data.products) m.set(p.product_code, p.image_url ?? null);
    return m;
  }, [data.products]);

  const sections = useMemo<PlanSection[]>(() => {
    if (!demand) return [];
    const out: PlanSection[] = [];
    for (const ym of demand.months) {
      const items: MonthPlanItem[] = [];
      for (const p of demand.products) {
        const c = p.cells[ym];
        if (!c || c.sold <= 0) continue;
        items.push({
          product_code: p.product_code,
          product_name: p.product_name,
          image_url: imageByCode.get(p.product_code) ?? null,
          month_sold: c.sold,
          demandLow: c.sold + c.lost_cons,
          demandHigh: c.sold + c.lost,
          revenue: c.sold_revenue + c.lost_revenue,
        });
      }
      if (items.length === 0) continue;
      // Priorita = obrat → řadíme dle tržeb (poptávky) sestupně.
      items.sort((a, b) => b.revenue - a.revenue);
      const allCount = items.length;
      const totalRevAll = items.reduce((s, it) => s + it.revenue, 0);

      // Pareto: nejmenší množina produktů tvořící 80 % obratu měsíce.
      let shown = items;
      if (paretoOnly && totalRevAll > 0) {
        const target = totalRevAll * 0.8;
        const kept: MonthPlanItem[] = [];
        let cum = 0;
        for (const it of items) {
          kept.push(it);
          cum += it.revenue;
          if (cum >= target) break;
        }
        shown = kept;
      }

      out.push({
        cal: Number(ym.slice(5, 7)),
        ym,
        items: shown,
        allCount,
        total: shown.reduce((s, it) => s + it.demandHigh, 0),
        totalRev: shown.reduce((s, it) => s + it.revenue, 0),
      });
    }
    const currentMonth = new Date().getMonth() + 1;
    const urgency = (c: number) => (c - currentMonth + 11) % 12; // příští měsíc první
    out.sort((a, b) => urgency(a.cal) - urgency(b.cal));
    return out;
  }, [demand, imageByCode, paretoOnly]);

  // PDF export plánu — agregace plochých dat na (měsíc, produkt) + Pareto (dle
  // aktuálního přepínače) + rozpad velikostí. Okno otevíráme synchronně.
  // Export JEDNOHO měsíce do PDF (z hlavičky sekce). Rychlé — 1 dotaz/měsíc.
  async function exportMonthPdf(ym: string, withPrices: boolean) {
    const w = openBlankWindow();
    setExportingPlan(true);
    try {
      const r = await apiFetch<{ rows: PlanExportRow[] }>(
        `/api/sales-history/plan-export?from=${ym}-01&to=${lastDayOfMonth(ym)}`,
      );
      const byParent = new Map<
        string,
        { name: string; demand: number; revenue: number; sizes: Map<string, number> }
      >();
      for (const row of r.rows) {
        let p = byParent.get(row.parent_code);
        if (!p) {
          p = { name: row.product_name, demand: 0, revenue: 0, sizes: new Map() };
          byParent.set(row.parent_code, p);
        }
        p.demand += row.demand;
        p.revenue += row.revenue;
        p.sizes.set(row.size, (p.sizes.get(row.size) ?? 0) + row.demand);
      }
      let products = [...byParent.entries()].map(([code, p]) => ({ code, ...p }));
      products.sort((a, b) => b.revenue - a.revenue);
      if (paretoOnly) {
        const totalRev = products.reduce((s, p) => s + p.revenue, 0);
        const target = totalRev * 0.8;
        const kept: typeof products = [];
        let cum = 0;
        for (const p of products) {
          kept.push(p);
          cum += p.revenue;
          if (cum >= target) break;
        }
        products = kept;
      }
      const cal = Number(ym.slice(5, 7));
      const monthData = {
        label: MONTH_NOM[cal - 1] ?? ym,
        deadline: `1. ${MONTH_GEN[cal - 1] ?? ''}`,
        products: products.map((p) => ({
          name: p.name,
          code: p.code,
          image: imageByCode.get(p.code) ?? null,
          demand: p.demand,
          revenue: p.revenue,
          sizes: [...p.sizes.entries()]
            .sort((a, b) => sizeNum(a[0]) - sizeNum(b[0]))
            .map(([size, qty]) => ({ size, qty })),
        })),
      };
      const title = `Plán přípravy — ${MONTH_NOM[cal - 1] ?? ym} ${withPrices ? '(s cenami)' : '(pro výrobu)'}`;
      if (w) printPlanInWindow(w, title, withPrices, [monthData]);
    } finally {
      setExportingPlan(false);
    }
  }

  if (loading) {
    return <div className="py-10 text-center text-sm text-text-muted">Sestavuji plán…</div>;
  }
  if (sections.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-text-muted">
        V tomto období žádné prodeje k naplánování.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PlanHelp />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-text-secondary">Zobrazit:</span>
        <div className="inline-flex overflow-hidden rounded-lg border border-border">
          {([true, false] as const).map((v) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => setParetoOnly(v)}
              className={`cursor-pointer px-3 py-1.5 text-xs font-medium transition-colors ${
                paretoOnly === v
                  ? 'bg-primary text-white'
                  : 'bg-surface text-text-secondary hover:bg-bg'
              }`}
            >
              {v ? 'Jen TOP (80 % obratu)' : 'Všechny produkty'}
            </button>
          ))}
        </div>
        {exportingPlan && (
          <span className="ml-auto text-[11px] text-text-muted">Připravuji PDF…</span>
        )}
      </div>
      {sections.map((sec, idx) => (
        <PlanMonthSection
          key={sec.ym}
          sec={sec}
          idx={idx}
          onExport={exportMonthPdf}
          exporting={exportingPlan}
        />
      ))}
    </div>
  );
}

function PlanMonthSection({
  sec,
  idx,
  onExport,
  exporting,
}: {
  sec: PlanSection;
  idx: number;
  onExport: (ym: string, withPrices: boolean) => void;
  exporting: boolean;
}) {
  const [open, setOpen] = useState(idx === 0);
  const expBtn =
    'cursor-pointer rounded border border-border bg-bg px-2 py-1 text-[11px] font-medium text-text-secondary transition-colors hover:bg-surface-hover hover:text-text disabled:cursor-not-allowed disabled:opacity-50';
  return (
    <div
      className={`rounded-xl border bg-surface ${idx === 0 ? 'border-primary/40' : 'border-border'}`}
    >
      <div className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-2 p-4">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex flex-1 cursor-pointer flex-wrap items-baseline gap-x-3 gap-y-1 text-left"
        >
          <span className="text-text-muted">{open ? '▾' : '▸'}</span>
          <span className="text-base font-bold uppercase text-text">
            {MONTH_NOM[sec.cal - 1] ?? ''}
          </span>
          <span className="text-sm font-medium text-primary">
            → mít hotovo do 1. {MONTH_GEN[sec.cal - 1] ?? ''}
          </span>
          {idx === 0 && (
            <span className="rounded bg-primary px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
              nejbližší
            </span>
          )}
          <span className="text-xs text-text-muted">
            · {sec.items.length}
            {sec.items.length < sec.allCount ? ` z ${sec.allCount}` : ''} produktů · ~
            {fmtNum(sec.totalRev)} Kč obratu · ~{fmtNum(sec.total)} ks
          </span>
        </button>
        <span className="flex shrink-0 items-center gap-1.5">
          <span className="text-[11px] font-semibold text-text-secondary">PDF:</span>
          <button
            type="button"
            onClick={() => onExport(sec.ym, true)}
            disabled={exporting}
            className={expBtn}
          >
            s cenami
          </button>
          <button
            type="button"
            onClick={() => onExport(sec.ym, false)}
            disabled={exporting}
            className={expBtn}
          >
            bez cen
          </button>
        </span>
      </div>
      {open && (
        <div className="space-y-2 border-t border-border px-4 pb-4 pt-3">
          {sec.items.map((it) => (
            <PlanRow key={it.product_code} item={it} ym={sec.ym} />
          ))}
        </div>
      )}
    </div>
  );
}

function ComingSoon({ tab }: { tab: TabId }) {
  const label = TABS.find((t) => t.id === tab)?.label ?? '';
  return (
    <div className="bg-surface border border-dashed border-border rounded-xl p-10 text-center">
      <div className="text-sm font-medium text-text">{label}</div>
      <div className="text-xs text-text-muted mt-1">Tab připravujeme — přijde v další iteraci.</div>
    </div>
  );
}
